import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import debounce from 'debounce-promise';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import Select from '@components/fields/Select';
import styles from '@components/forms/careProfile/CareProfile.module.css';
import CareProfileFormCondition from '@components/forms/careProfile/CareProfileFormCondition';
import Form from '@components/forms/Form';
import {
  CareProfileAcuityEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForAcuity } from '@utils/optionUtils';

type CareProfileFieldRules = GetCareProfileFieldRulesQuery['careProfileFieldRules'];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery['journey'];
};

type FormData = {
  acuity?: CareProfileAcuityEnum | null;
};

const CareProfileRiskForm = ({ careProfileFieldRules, journey }: Props) => {
  const intl = useIntl();

  const careProfile = journey.careProfile;

  const defaultValues: FormData = {
    acuity: careProfile.acuity ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      acuity: yup.string().nullable().oneOf(Object.values(CareProfileAcuityEnum))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    setBaseErrors([]);
    return updateCareProfile({
      variables: {
        ...form.getValues(),
        id: journey.careProfile.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  useEffect(() => {
    const debouncedSubmit = debounce(submitCareProfile, 2000);
    const subscription = form.watch(() => form.handleSubmit(debouncedSubmit)());
    return () => subscription.unsubscribe();
  }, [form, submitCareProfile]);

  const acuityOptions = optionsForAcuity(intl);

  return (
    <Form hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.formSectionRow}>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.acuity}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="acuity"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.acuity}
              label={intl.formatMessage({ id: 'forms.careProfileNew.acuityLabel' })}
              name="acuity"
              options={acuityOptions}
              tabIndex={0}
            />
          </CareProfileFormCondition>
        </div>
      </div>
    </Form>
  );
};

export default CareProfileRiskForm;
